import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Dashboard } from './screens/Dashboard';
import { TreasuryBalances } from './screens/TreasuryBalances';
import { TreasuryRebalance } from './screens/TreasuryRebalance';
import { RecordMoney } from './screens/RecordMoney';
import { AdjustBalance } from './screens/AdjustBalance';
import { Parties } from './screens/Parties';
import { PartyDetail } from './screens/PartyDetail';
import { ConvertBalance } from './screens/ConvertBalance';
import { Swap } from './screens/Swap';
import { SubBrokerTrade } from './screens/SubBrokerTrade';
import { NewParty } from './screens/NewParty';
import { NewTrade } from './screens/NewTrade';
import { ManagerCheque } from './screens/ManagerCheque';
import { ChainActivity } from './screens/ChainActivity';
import { Exchanges } from './screens/Exchanges';
import { McDeal } from './screens/McDeal';
import { InterEntityTx } from './screens/InterEntityTx';
import { TradesLog } from './screens/TradesLog';
import { Movements } from './screens/Movements';
import { Settlements } from './screens/Settlements';
import { Profit } from './screens/Profit';
import { TradingReport } from './screens/TradingReport';
import { BalanceSheet } from './screens/BalanceSheet';
import { Periods } from './screens/Periods';
import { Migration } from './screens/Migration';
import { AttachNames } from './screens/AttachNames';
import { Inventory } from './screens/Inventory';
import { WebLogin } from './screens/WebLogin';
import { Kyt } from './screens/Kyt';
import { Onboarding } from './screens/Onboarding';
import { Energy } from './screens/Energy';
import { CustodySection } from './screens/custody/CustodySection';
// Batch 4 · custody (the UI rebuild's hand-off, adopted by the wallet lane 27 Oct 2026): operations and setup.
// Drawn only when custody is in the console AND batch 4 is on — the owner's batch-4 preview alone draws today's.
import { CustodyV2 } from './screens/v2/custody/Custody';
import { CustodyAdminRoutes } from './screens/v2/custody-admin/Section';
import { CUSTODY_IN_CONSOLE } from './lib/routes';
import { WalletRegister } from './screens/WalletRegister';
import { ConsoleShell } from './components/ConsoleShell';
import { ScreenBoundary } from './components/ScreenBoundary';
import { getWebSession, isWebContext } from './lib/webSession';
import { uiV2, PREVIEW_OFFERED } from './lib/ui-v2';
import { UiPreview } from './screens/v2/UiPreview';
import { MoreHub } from './screens/v2/MoreHub';
import { NewEntry } from './screens/v2/NewEntry';
// Batch 2 — daily work (lib/ui-v2.ts): each route draws its rebuilt screen while the switch is on, today's otherwise.
import { HomeV2 } from './screens/v2/Home';
import { PartiesV2 } from './screens/v2/Parties';
import { PartyV2 } from './screens/v2/Party';
import { TradesV2 } from './screens/v2/Trades';
import { MovementsV2 } from './screens/v2/Movements';
import { TreasuryV2 } from './screens/v2/Treasury';
import { ChainV2 } from './screens/v2/Chain';
// Batch 3 — the entry forms and Settle later (lib/ui-v2.ts). The cheque's progress (McDeal) is not in it.
import { BookTradeV2 } from './screens/v2/BookTrade';
import { RecordV2 } from './screens/v2/Record';
import { MoveV2 } from './screens/v2/Move';
import { ConvertV2 } from './screens/v2/Convert';
import { ConvertPartyV2 } from './screens/v2/ConvertParty';
import { SubBrokerV2 } from './screens/v2/SubBroker';
import { ChequeV2 } from './screens/v2/Cheque';
import { AdjustV2 } from './screens/v2/Adjust';
import { NewPartyV2 } from './screens/v2/NewParty';
import { SettleV2 } from './screens/v2/Settle';
// Batch 5 — Onboarding, from the onboarding lane's checklist spec (lib/ui-v2.ts).
import { OnboardingV2 } from './screens/v2/Onboarding';
import { ProfitV2 } from './screens/v2/Profit';
import { InventoryV2 } from './screens/v2/Inventory';
import { BalanceSheetV2 } from './screens/v2/BalanceSheet';
import { TradingV2 } from './screens/v2/Trading';
import { CloseV2 } from './screens/v2/Close';
import { ExchangesV2 } from './screens/v2/Exchanges';
import { WalletsV2 } from './screens/v2/Wallets';
import { LegacyV2 } from './screens/v2/Legacy';
import { NamesV2 } from './screens/v2/Names';
import { SignInV2 } from './screens/v2/SignIn';
// Cheque progress (McDeal) — after P-042's held press landed; its stage forms are McDeal's own, mounted as they are.
import { ChequeProgressV2 } from './screens/v2/ChequeProgress';
// Batch 4 · KYT (area kyt): the KYT lane's rebuilt screen — the payments that need him and their assessment (screens/v2/kyt).
import { KytV2 } from './screens/v2/kyt/Kyt';
// Batch 4 · energy (area energy): the energy lane's rebuilt screen — the list, Top up, and a wallet's standing order (screens/v2/energy).
import { EnergyV2 } from './screens/v2/energy/Energy';
import { EnergyRentV2 } from './screens/v2/energy/Rent';
import { EnergySettingsV2 } from './screens/v2/energy/Settings';

// Admin-only operator console (2026-05-23 architecture revision: KYC + KYT
// live in a separate worker/Mini App + bot ecosystem — not this surface).
// The /kyt screen READS that worker's picture over the BFF; it screens nothing.
export function App() {
  // A tab left open across a deploy keeps running the OLD bundle — that is how
  // 207,955 USDT booked through a superseded code path on 2026-08-22. The
  // newer-build bar and the reload on the next route change live in
  // ConsoleShell (lib/useNewerBuild.ts).

  // Telegram WebView on iOS doesn't auto-scroll a focused input above the
  // soft keyboard. Listen globally and bring the focused field into view.
  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      // Defer so the keyboard has time to animate in.
      setTimeout(() => {
        t.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 150);
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, []);

  // Web console guard: outside Telegram a stored session is required before
  // any screen renders. initData context never reaches the login path.
  const isWeb = isWebContext();
  const [authed, setAuthed] = useState(() => getWebSession() !== null);
  if (isWeb && !authed) {
    return uiV2(5) ? <SignInV2 onAuthed={() => setAuthed(true)} /> : <WebLogin onAuthed={() => setAuthed(true)} />;
  }

  return (
    <ConsoleShell>
      <ScreenBoundary>
      <Routes>
        <Route path="/"                     element={uiV2(2) ? <HomeV2 /> : <Dashboard />} />
        <Route path="/treasury"             element={uiV2(2) ? <TreasuryV2 /> : <TreasuryBalances />} />
        <Route path="/treasury/rebalance"   element={uiV2(3) ? <MoveV2 /> : <TreasuryRebalance />} />
        {/* One screen, five kinds of movement. The mode routes exist so the
            ⌘K palette can land straight on one; `key` forces a remount when
            the operator jumps between them, so no form state carries over.
            The fifth, Between parties, rides /record?mode=p2p (&from=&to=)
            instead of a path — the party page links to it with the party. */}
        <Route path="/record"               element={uiV2(3) ? <RecordV2 key="customer" /> : <RecordMoney key="customer" />} />
        <Route path="/record/expense"       element={uiV2(3) ? <RecordV2 key="expense" initialMode="expense" /> : <RecordMoney key="expense" initialMode="expense" />} />
        <Route path="/record/capital"       element={uiV2(3) ? <RecordV2 key="capital" initialMode="capital" /> : <RecordMoney key="capital" initialMode="capital" />} />
        <Route path="/record/drawing"       element={uiV2(3) ? <RecordV2 key="drawing" initialMode="drawing" /> : <RecordMoney key="drawing" initialMode="drawing" />} />
        <Route path="/treasury/adjust"      element={uiV2(3) ? <AdjustV2 /> : <AdjustBalance />} />
        {/* Read-only report: income − costs, with the owner's capital and
            drawings kept deliberately outside the profit figure. */}
        <Route path="/profit"               element={uiV2(5) ? <ProfitV2 /> : <Profit />} />
        <Route path="/trading"              element={uiV2(5) ? <TradingV2 /> : <TradingReport />} />
        {/* Read-only report: a POSITION at an instant, not a period — what the
            desk owns, owes and is worth. Kept apart from Profit on purpose: the
            two disagree today, because realised margin is a memo pair that never
            reaches equity and there is no period close to move it. */}
        <Route path="/balance-sheet"        element={uiV2(5) ? <BalanceSheetV2 /> : <BalanceSheet />} />
        <Route path="/periods"              element={uiV2(5) ? <CloseV2 /> : <Periods />} />
        <Route path="/migration"            element={uiV2(5) ? <LegacyV2 /> : <Migration />} />
        <Route path="/attach-names"         element={uiV2(5) ? <NamesV2 /> : <AttachNames />} />
        {/* Read-only report: the working behind the profit's spread line — one
            row per inventory book (asset × the currency it was bought with),
            with an assumed cost basis never allowed to look like a measured one. */}
        <Route path="/inventory"            element={uiV2(5) ? <InventoryV2 /> : <Inventory />} />
        <Route path="/parties"              element={uiV2(2) ? <PartiesV2 /> : <Parties />} />
        <Route path="/parties/new"          element={uiV2(3) ? <NewPartyV2 /> : <NewParty />} />
        <Route path="/parties/:slug"        element={uiV2(2) ? <PartyV2 /> : <PartyDetail />} />
        {/* One party's box, one asset into another at a stated rate — booked
            as a trade settled on account, no drawer (11 Sept 2026). */}
        <Route path="/parties/:slug/convert" element={uiV2(3) ? <ConvertPartyV2 /> : <ConvertBalance />} />
        {/* Every convertible balance on one screen (12 Sept 2026, evening):
            parties and desk accounts on the left, the matching form on the
            right — the party form above for a box, a settled trade with the
            money changer for a desk account. ?party= / ?rail=&asset= preselect. */}
        <Route path="/swap"                 element={uiV2(3) ? <ConvertV2 /> : <Swap />} />
        {/* A merchant dealing with his own clients on the desk's coins: his
            settle-later trade with the desk at the desk rate, the client's
            money in and the coins out, as three ordinary entries in one go —
            his margin stays on his box (12 Sept 2026, "Loki as sub-broker"). */}
        <Route path="/parties/:slug/sub-broker" element={uiV2(3) ? <SubBrokerV2 /> : <SubBrokerTrade />} />
        <Route path="/trade"                element={uiV2(3) ? <BookTradeV2 /> : <NewTrade />} />
        <Route path="/trades"               element={uiV2(2) ? <TradesV2 /> : <TradesLog />} />
        {/* The other history: what MOVED, as against what was agreed. Reads
            GET /api/movements — filtered, paged, and totalled over the whole
            filtered set. Accepts ?party= / ?rail= / ?asset= / ?kind= / ?from=
            / ?to= as its opening filters, which is how a party's statement
            links through to it. */}
        <Route path="/movements"            element={uiV2(2) ? <MovementsV2 /> : <Movements />} />
        <Route path="/settle"               element={uiV2(3) ? <SettleV2 /> : <Settlements />} />
        <Route path="/mc"                   element={uiV2(3) ? <ChequeV2 /> : <ManagerCheque />} />
        <Route path="/chain"                element={uiV2(2) ? <ChainV2 /> : <ChainActivity />} />
        <Route path="/exchanges"            element={uiV2(5) ? <ExchangesV2 /> : <Exchanges />} />
        {/* Read-only window onto the screening worker: watched wallets, flags,
            onboarding funnel, and the Books' own wallet register beside them. */}
        <Route path="/kyt"                  element={uiV2(4) ? <KytV2 /> : <Kyt />} />
        {/* Every onboarding lead that needs a person, each in full, and every
            approved client, as the onboarding worker lists them. Read-only:
            decisions stay in the Telegram ops group. */}
        <Route path="/onboarding"           element={uiV2(5) ? <OnboardingV2 /> : <Onboarding />} />
        {/* The energy desk: the prepaid balance, the treasury wallets' energy,
            and the rentals. A separate service; the Books only show it. */}
        <Route path="/energy"               element={uiV2(4) ? <EnergyV2 /> : <Energy />} />
        {/* Batch 4: the rebuilt screen's two pages of its own — Top up, and a wallet's standing order. Off, no such path. */}
        {uiV2(4) && <Route path="/energy/rent" element={<EnergyRentV2 />} />}
        {uiV2(4) && <Route path="/energy/settings/:walletId" element={<EnergySettingsV2 />} />}
        {/* The wallet register: every address the books know; retire and
            archive go one way, nothing is deleted (db/0064). */}
        <Route path="/wallets"              element={uiV2(5) ? <WalletsV2 /> : <WalletRegister />} />
        {CUSTODY_IN_CONSOLE && uiV2(4) && <Route path="/custody-admin/*" element={<CustodyAdminRoutes />} />}
        <Route path="/custody/*"            element={CUSTODY_IN_CONSOLE && uiV2(4) ? <CustodyV2 /> : <CustodySection />} />
        <Route path="/mc/:dealId"           element={uiV2(5) ? <ChequeProgressV2 /> : <McDeal />} />
        {/* chain-swap: the instrument was designed OUT of the fresh books
            (chain lives in the wallet name; a swap = two ordinary Moves). The
            screen died with it — the route lands on Move money instead. */}
        <Route path="/treasury/chain-swap"  element={uiV2(3) ? <MoveV2 /> : <TreasuryRebalance />} />
        {/* The rebuild's batch 1 (lib/ui-v2.ts): its two hubs, and no inter-entity door — the other desk is closed
            (IndiaOTC ruling, 18 Oct 2026; plan verification 1.4). Off, the routes are main's. */}
        {!uiV2(1) && <Route path="/inter-entity" element={<InterEntityTx />} />}
        {/* The owner's switch for the rebuilt console, on this device (lib/ui-v2.ts PREVIEWABLE). */}
        {PREVIEW_OFFERED && <Route path="/ui-preview" element={<UiPreview />} />}
        {uiV2(1) && <Route path="/more" element={<MoreHub />} />}
        {uiV2(1) && <Route path="/new" element={<NewEntry />} />}
        {isWeb && <Route path="/login"      element={uiV2(5) ? <SignInV2 /> : <WebLogin />} />}
        {/* Unknown URL: land Home, never a silent black page (found 2 Sept —
            a mistyped path rendered the shell around nothing). */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ScreenBoundary>
    </ConsoleShell>
  );
}
